import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Op } from 'quill/core';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import AnnotationIcon from './AnnotationIcon';
import AnnotationCreate from './AnnotationCreate';
import EditorActions from '../../editor/EditorActions';
import { CREATION_ID } from '../../editor/editorReducer';
import type {
  Annotation_AnnotationFragment,
  AnnotationCreate_AnnotationFragment,
} from '../../graphql/generated/graphqlTypes';
import AnnotationPreview from './AnnotationPreview';
import AnnotationLinkInput from './AnnotationLinkInput';
import AnnotationLink from './AnnotationLink';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import styles from './Annotation.module.scss';
import OverflowMenu from '../../components/OverflowMenu';
import Button from '../../components/Button';
import SVGIcon from '../../components/SVGIcon';
import overflowMenuIcon from '../../icons/overflowMenu.svg';
import { MenuItem } from '../../components/Menu';
import editIcon from '../../icons/edit.svg';
import AnnotationDeleteMenuItem from './AnnotationDeleteMenuItem';
import useAnnotationLink from './useAnnotationLink';
import showFullPageSheet from '../comment/showFullPageSheet';
import addTestId from '../../utils/addTestId';

const ANNOTATION_FRAGMENT = gql`
  fragment Annotation_annotation on Annotation {
    id
    ...AnnotationPreview_annotation
  }
  ${AnnotationPreview.fragments.annotation}
`;

interface AnnotationProps {
  active: boolean;
  minimized: boolean;
  annotation: Annotation_AnnotationFragment;
  onResize?: () => void;
  unsavedInputValue?: Op[];
  onInputChange: (content: Op[]) => void;
  postId: string;
  popOverOpen: boolean;
  size: MagneticWallItemSize;
}

const Annotation = ({
  active,
  minimized,
  annotation,
  onResize,
  unsavedInputValue = [],
  onInputChange,
  postId,
  popOverOpen,
  size,
}: AnnotationProps) => {
  const isNewAnnotation = annotation.id === CREATION_ID;
  const dispatch = useDispatch();

  const [isLinkInputOpen, setIsLinkInputOpen] = useState(false);
  const annotationLinkState = useAnnotationLink({
    id: annotation.id,
    initialValue: annotation.link ?? '',
    onUpdated() {
      setIsLinkInputOpen(false);
    },
  });

  const [isEditing, setIsEditing] = useState(isNewAnnotation);
  const [isShowingOverflowMenuButton, setIsShowingOverflowMenuButton] =
    useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  useEffect(() => {
    onResize?.();

    if (!active) {
      setIsEditing(false);
    }
  }, [onResize, active, isHovered]);

  useEffect(() => {
    if (!isEditing) {
      setIsLinkInputOpen(false);
    }
  }, [isEditing]);

  useEffect(() => {
    if (isHovered) {
      dispatch(EditorActions.highlightAnnotation(annotation.id));
    } else {
      dispatch(EditorActions.removeMagneticWallHighlight());
    }
  }, [dispatch, annotation.id, isHovered]);

  const onDismiss = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const focusAnnotationById = (id: string) => {
    dispatch(EditorActions.focusAnnotation(id));
  };

  const focusCurrentAnnotation = () => focusAnnotationById(annotation.id);

  const innerFullView = (
    <div className={styles.innerFullView}>
      {isEditing ? (
        <div className={styles.createContainer}>
          <AnnotationCreate
            annotationId={annotation.id}
            initialContent={
              isEditing
                ? annotation.content ?? unsavedInputValue
                : unsavedInputValue
            }
            onChange={onInputChange}
            postId={postId}
            onSaved={(savedAnnotation: AnnotationCreate_AnnotationFragment) => {
              focusAnnotationById(savedAnnotation.id);
              onInputChange?.([]);
              setIsEditing(false);
            }}
            onReferenceButtonClick={() =>
              setIsLinkInputOpen((isOpen) => !isOpen)
            }
            link={annotationLinkState.link}
          />
        </div>
      ) : (
        <div
          onMouseEnter={() => setIsShowingOverflowMenuButton(true)}
          onMouseLeave={() => setIsShowingOverflowMenuButton(false)}
          className={styles.previewContainer}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited */}
          <div onClick={focusCurrentAnnotation} className={styles.preview}>
            <AnnotationPreview annotation={annotation} />
          </div>
          {(isShowingOverflowMenuButton || isOverflowMenuOpen) && (
            <div className={styles.overflowMenuContainer}>
              <OverflowMenu
                inline={false}
                open={isOverflowMenuOpen}
                onOpenChange={setIsOverflowMenuOpen}
                button={
                  <Button
                    testId="annotation-overflow-menu-button"
                    className={classNames({
                      [styles.active]: isOverflowMenuOpen,
                    })}
                  >
                    <SVGIcon coloringWith="fill">{overflowMenuIcon}</SVGIcon>
                  </Button>
                }
              >
                <MenuItem
                  icon={editIcon}
                  onClick={() => {
                    focusCurrentAnnotation();
                    setIsEditing(true);
                  }}
                >
                  Edit annotation
                </MenuItem>
                <AnnotationDeleteMenuItem
                  onDeleted={onDismiss}
                  annotationId={annotation.id}
                />
              </OverflowMenu>
            </div>
          )}
        </div>
      )}
      {!isLinkInputOpen && annotationLinkState.link && (
        <AnnotationLink link={annotationLinkState.link} />
      )}
    </div>
  );

  const annotationLinkInput = (
    <AnnotationLinkInput
      value={annotationLinkState.value}
      onChange={annotationLinkState.onChange}
      onSave={annotationLinkState.onSave}
      onRemoveLink={annotationLinkState.onRemove}
    />
  );

  return (
    <MagneticWallItem
      minimized={minimized}
      focused={active}
      isPopOverOpen={popOverOpen}
      id={`annotation-${annotation.id}`}
      onBlur={onDismiss}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      popOverContent={
        <>
          <PopOverContainer noPadding width="auto">
            {innerFullView}
          </PopOverContainer>
          {isLinkInputOpen && (
            <div className={styles.popoverLinkInputContainer}>
              <PopOverContainer noPadding width="auto">
                {annotationLinkInput}
              </PopOverContainer>
            </div>
          )}
        </>
      }
      fullView={
        <div>
          {isHovered || active ? (
            <>
              {innerFullView}
              {isLinkInputOpen && annotationLinkInput}
            </>
          ) : (
            <div
              {...addTestId('annotation-icon')}
              className={styles.fullAnnotationIconContainer}
            >
              <AnnotationIcon />
            </div>
          )}
        </div>
      }
      compactView={
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited
        <div
          {...addTestId('annotation-icon')}
          className={classNames(styles.compactAnnotationIconContainer, {
            [styles.ring]: active && showFullPageSheet(),
          })}
          onClick={focusCurrentAnnotation}
        >
          <AnnotationIcon />
        </div>
      }
      size={size}
    />
  );
};

Annotation.fragments = { annotation: ANNOTATION_FRAGMENT };

export default Annotation;
